import React, { useMemo, useState } from "react";
import Icons from "../icons";
import { ActivityButton, formatWhen } from "./ActivityPanel";
import { stepToScript } from "../stepEnglish";

/* How the API writes a secret value (activity_service.MASK). */
const MASK = "••••";

/* App Page Activity — the Requirement Board's and Artifacts screen's Activity
   (ActivityPanel), for App Pages: who created, recreated and ran an Artifact,
   its Test and its executions, and when (GET app-pages/activity, read-only).
   App Pages keep their own log (no Jira/Xray keys), read through the same
   panel; an entry that replaced something also shows Before / After. */

export const APP_PAGE_ACTIONS = {
  "test.created": { label: "Test created", Icon: Icons.Plus },
  "test.recreated": { label: "Test re-created", Icon: Icons.Recreate },
  "test.restored": { label: "Test active again", Icon: Icons.Check },
  "test.retired": { label: "Test retired", Icon: Icons.Minus },
  "test.deleted": { label: "Test deleted", Icon: Icons.Trash },
  "test.values_changed": { label: "Variable values changed", Icon: Icons.Variable },
  "test.auto_fixed": { label: "Scenario repaired by AI", Icon: Icons.Spark },
  "artifact.created": { label: "Artifact created", Icon: Icons.Plus },
  "artifact.recreated": { label: "Artifact re-created", Icon: Icons.Recreate },
  "artifact.values_changed": { label: "Script given new values", Icon: Icons.Variable },
  "artifact.deleted": { label: "Artifact deleted", Icon: Icons.Trash },
  "artifact.auto_fixed": { label: "Script repaired by AI", Icon: Icons.Spark },
  "execution.created": { label: "Execution created", Icon: Icons.Plus },
  "execution.started": { label: "Execution started", Icon: Icons.Play },
  "execution.start_refused": { label: "Execution not started", Icon: Icons.Lock },
  "execution.finished": { label: "Execution finished", Icon: Icons.Check },
  "execution.deleted": { label: "Execution deleted", Icon: Icons.Trash },
  /* The page's Feature (the AppPage QA Agent's suite) and the runner's runs: seen in the App Page's own Activity. */
  "suite.created": { label: "Feature saved", Icon: Icons.Plus },
  "suite.updated": { label: "Feature edited", Icon: Icons.Pencil },
  "suite.deleted": { label: "Feature deleted", Icon: Icons.Trash },
  "run.started": { label: "Runner run queued", Icon: Icons.Play },
  "run.finished": { label: "Runner run finished", Icon: Icons.Check },
  "run.deleted": { label: "Runner run deleted", Icon: Icons.Trash },
};

/* The screen's filters, by what happened to a Test ID, each with the icon its
   entries carry. */
export const APP_PAGE_GROUPS = [
  { id: "", label: "All", Icon: Icons.List },
  { id: "created", label: "Created", Icon: Icons.Plus, actions: ["test.created"] },
  { id: "recreated", label: "Recreated", Icon: Icons.Recreate, actions: ["test.recreated"] },
  { id: "values", label: "Variables", Icon: Icons.Variable, actions: ["test.values_changed", "artifact.values_changed"] },
  { id: "deleted", label: "Deleted", Icon: Icons.Trash, actions: ["test.deleted", "artifact.deleted", "execution.deleted"] },
];

/* What an entry is about, as colours: its icon's and its row's left line.
   The icon says what happened (APP_PAGE_ACTIONS: + created, the two-arrow
   cycle re-created…; its label is the icon's tooltip); the row says the rest,
   starting with the ID. */
const ITEMS = {
  test: {
    cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/25 dark:bg-emerald-500/15 dark:text-emerald-300",
    bar: "border-emerald-500",
  },
  values: {
    cls: "bg-amber-50 text-amber-800 ring-amber-600/25 dark:bg-amber-500/15 dark:text-amber-300",
    bar: "border-amber-500",
  },
  artifact: {
    cls: "bg-brand-500/10 text-brand-700 ring-brand-600/25 dark:text-brand-300",
    bar: "border-brand-500",
  },
  execution: {
    cls: "bg-sky-50 text-sky-700 ring-sky-600/25 dark:bg-sky-500/15 dark:text-sky-300",
    bar: "border-sky-500",
  },
  suite: {
    cls: "bg-violet-50 text-violet-700 ring-violet-600/25 dark:bg-violet-500/15 dark:text-violet-300",
    bar: "border-violet-500",
  },
  run: {
    cls: "bg-teal-50 text-teal-700 ring-teal-600/25 dark:bg-teal-500/15 dark:text-teal-300",
    bar: "border-teal-500",
  },
};

/* The word a title ends with, by what happened, and its colour. */
const WORDS = {
  created: "text-emerald-700 dark:text-emerald-300",
  recreated: "text-brand-700 dark:text-brand-300",
  changed: "text-amber-700 dark:text-amber-300",
  deleted: "text-rose-700 dark:text-rose-300",
  retired: "text-rose-700 dark:text-rose-300",
};

export function appPageKind(e) {
  const [thing, verb = ""] = String(e.action || "").split(".");
  const item = verb === "values_changed" ? ITEMS.values : ITEMS[thing] || ITEMS.test;
  const word = verb === "values_changed" ? "changed" : verb;
  return { ...item, Icon: APP_PAGE_ACTIONS[e.action]?.Icon || Icons.Log, ...(WORDS[word] ? { word, wordCls: WORDS[word] } : {}) };
}

/* One change of variable values is two entries, written together: the Test's
   new values, then its script given them. The list shows them as one (the
   values, with `script` the script's entry); the CSV keeps both. */
const TOGETHER_MS = 2 * 60 * 1000;
export function combineAppPageItems(items) {
  const scripts = items.filter((e) => e.action === "artifact.values_changed");
  if (!scripts.length) return items;
  const used = new Set();
  const out = items.map((e) => {
    if (e.action !== "test.values_changed") return e;
    const at = new Date(e.occurred_at).getTime();
    const script = scripts.find(
      (a) =>
        !used.has(a) &&
        a.test_key === e.test_key &&
        (a.user_name || "") === (e.user_name || "") &&
        Math.abs(new Date(a.occurred_at).getTime() - at) <= TOGETHER_MS,
    );
    if (!script) return e;
    used.add(script);
    return { ...e, artifact_key: e.artifact_key || script.artifact_key, script };
  });
  return out.filter((e) => !used.has(e));
}

/* On App Page Executions the runs matter too: one more filter, for every
   execution entry (created, started, not started, finished). */
export const APP_PAGE_EXECUTION_GROUPS = [...APP_PAGE_GROUPS, { id: "execution", label: "Executions", Icon: Icons.Play }];

export const appPageFacts = (e) => [
  ["App Page", e.app_page_id],
  ["Test", e.test_key],
  ["Artifact", e.artifact_key],
  ["Execution", e.execution_key],
];

/* What a stored value is called, in the order shown. */
const FIELDS = [
  ["gherkin", "Scenario"],
  ["script", "Playwright script"],
  ["steps", "Steps"],
  ["variables", "Variables"],
  ["name", "Name"],
  ["title", "Title"],
  ["totals", "Totals"],
  ["message", "Message"],
  ["duration_s", "Duration (s)"],
  ["started_at", "Started"],
  ["finished_at", "Finished"],
];

function asText(key, value) {
  if (value == null || value === "") return "";
  if (key === "steps" && Array.isArray(value)) {
    return value
      .map((s, i) => `${i + 1}. ${stepToScript(s) || [s.keyword, s.text].filter(Boolean).join(" ") || JSON.stringify(s)}`)
      .join("\n");
  }
  if (key === "variables" && typeof value === "object") {
    return Object.entries(value)
      .map(([name, v]) => `${name} = ${v && typeof v === "object" ? v.value ?? "" : v}`)
      .join("\n");
  }
  if (key === "started_at" || key === "finished_at") return formatWhen(value);
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join("\n");
  }
  return String(value);
}

/* The fields an entry kept: for a change, only those that differ (plain values,
   such as variable values, when the entry is not a snapshot). */
export function changesOf(e) {
  const before = e.before && typeof e.before === "object" ? e.before : null;
  const after = e.after && typeof e.after === "object" ? e.after : null;
  if (!before && !after) return [];
  const known = FIELDS.filter(([k]) => (before && k in before) || (after && k in after));
  if (!known.length) {
    const names = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
    return names.map((n) => ({ key: n, label: n, before: before ? asText(n, before[n]) : null, after: after ? asText(n, after[n]) : null }));
  }
  return known
    .map(([k, label]) => ({ key: k, label, before: before ? asText(k, before[k]) : null, after: after ? asText(k, after[k]) : null }))
    .filter((c) => (before && after ? c.before !== c.after : c.before || c.after));
}

/* Line diff (longest common subsequence) of two texts: rows of
   {t: "same" | "del" | "add", text, o, n} with old and new line numbers, or
   null when the texts are too long to compare here. */
export function lineDiff(before, after) {
  const A = String(before ?? "").split("\n");
  const B = String(after ?? "").split("\n");
  let s = 0;
  while (s < A.length && s < B.length && A[s] === B[s]) s++;
  let t = 0;
  while (t < A.length - s && t < B.length - s && A[A.length - 1 - t] === B[B.length - 1 - t]) t++;
  const a = A.slice(s, A.length - t);
  const b = B.slice(s, B.length - t);
  if ((a.length + 1) * (b.length + 1) > 400000) return null;
  const w = b.length + 1;
  const dp = new Uint32Array((a.length + 1) * w);
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--)
      dp[i * w + j] = a[i] === b[j] ? dp[(i + 1) * w + j + 1] + 1 : Math.max(dp[(i + 1) * w + j], dp[i * w + j + 1]);
  const rows = [];
  let o = 0;
  let n = 0;
  const same = (text) => rows.push({ t: "same", text, o: ++o, n: ++n });
  A.slice(0, s).forEach(same);
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      same(a[i]);
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && dp[(i + 1) * w + j] >= dp[i * w + j + 1])) {
      rows.push({ t: "del", text: a[i++], o: ++o, n: null });
    } else {
      rows.push({ t: "add", text: b[j++], o: null, n: ++n });
    }
  }
  A.slice(A.length - t).forEach(same);
  return rows;
}

const CONTEXT = 2;

/* Unchanged runs longer than the context around a change fold into one row. */
function folded(rows) {
  const out = [];
  let k = 0;
  while (k < rows.length) {
    if (rows[k].t !== "same") {
      out.push(rows[k++]);
      continue;
    }
    let end = k;
    while (end < rows.length && rows[end].t === "same") end++;
    const run = rows.slice(k, end);
    const head = k === 0 ? 0 : CONTEXT;
    const tail = end === rows.length ? 0 : CONTEXT;
    if (run.length > head + tail + 1) {
      out.push(...run.slice(0, head), { t: "gap", count: run.length - head - tail }, ...run.slice(run.length - tail));
    } else {
      out.push(...run);
    }
    k = end;
  }
  return out;
}

const counts = (rows) => ({
  added: rows.filter((r) => r.t === "add").length,
  removed: rows.filter((r) => r.t === "del").length,
});

function Counts({ added, removed }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[0.6875rem] font-semibold">
      <span className="text-emerald-700 dark:text-emerald-300" title={`${added} line${added === 1 ? "" : "s"} added`}>+{added}</span>
      <span className="text-rose-700 dark:text-rose-300" title={`${removed} line${removed === 1 ? "" : "s"} removed`}>−{removed}</span>
    </span>
  );
}

const ROW = {
  same: "text-slate-700",
  del: "bg-rose-50 text-rose-900 dark:bg-rose-500/15 dark:text-rose-200",
  add: "bg-emerald-50 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200",
};
const SIGN = { same: " ", del: "−", add: "+" };

function Block({ title, text, tone }) {
  return (
    <div className="min-w-0 flex-1">
      <p className={`mb-1 text-[0.6875rem] font-medium capitalize ${tone}`}>{title}</p>
      <pre className="cq-scroll max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-2 font-mono text-[0.6875rem] leading-relaxed text-slate-700">
        {text || "—"}
      </pre>
    </div>
  );
}

function GapButton({ count, onClick }) {
  return (
    <button type="button" onClick={onClick} className="block w-full bg-slate-50 px-3 py-0.5 text-left font-sans text-[0.6875rem] text-brand-700 hover:bg-slate-100">
      <span className="inline-flex items-center gap-1"><Icons.Ellipsis className="h-3.5 w-3.5" /> {count} unchanged line{count === 1 ? "" : "s"}, show all</span>
    </button>
  );
}

/* Diff rows as side-by-side pairs: an unchanged line on both sides, a run of
   removed lines beside the run of added lines that replaced it. */
export function sideBySide(rows) {
  const out = [];
  let k = 0;
  while (k < rows.length) {
    const r = rows[k];
    if (r.t === "gap") {
      out.push({ gap: r });
      k++;
    } else if (r.t === "same") {
      out.push({ left: r, right: r });
      k++;
    } else {
      const del = [];
      const add = [];
      while (k < rows.length && rows[k].t === "del") del.push(rows[k++]);
      while (k < rows.length && rows[k].t === "add") add.push(rows[k++]);
      for (let i = 0; i < Math.max(del.length, add.length); i++) out.push({ left: del[i] || null, right: add[i] || null });
    }
  }
  return out;
}

function SplitCell({ line, right = false }) {
  const edge = right ? "border-l border-slate-200" : "";
  if (!line) return <div className={`${edge} bg-slate-50/70`} />;
  const t = line.t;
  return (
    <div className={`flex min-w-0 ${edge} ${ROW[t]}`}>
      <span aria-hidden="true" className="w-9 flex-none select-none pr-1 text-right text-slate-400">{(right ? line.n : line.o) ?? ""}</span>
      <span aria-label={t === "del" ? "removed" : t === "add" ? "added" : undefined} className="w-4 flex-none select-none text-center">
        {SIGN[t]}
      </span>
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words pr-3">{line.text || " "}</span>
    </div>
  );
}

/* One changed field: a line diff (changes with a little context, or every
   line), or the two versions side by side, the changed lines marked on both. */
function FieldDiff({ change }) {
  const [view, setView] = useState("diff");
  const [all, setAll] = useState(false);
  const rows = useMemo(() => lineDiff(change.before, change.after), [change.before, change.after]);
  const tabCls = (on) => `px-2 py-0.5 text-[0.6875rem] font-medium ${on ? "bg-brand-600 text-white" : "bg-card text-slate-600 hover:bg-slate-50"}`;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5">
        <span className="text-[0.75rem] font-semibold text-slate-800">{change.label}</span>
        {rows && <Counts {...counts(rows)} />}
        <span className="ml-auto inline-flex items-center gap-2 text-[0.6875rem] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="text-rose-700 dark:text-rose-300">−</span>
            <span>Before</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="text-emerald-700 dark:text-emerald-300">+</span>
            <span>After</span>
          </span>
        </span>
        {rows && (
          <div role="group" aria-label={`How to show ${change.label}`} className="inline-flex overflow-hidden rounded-md ring-1 ring-slate-200">
            <button type="button" aria-pressed={view === "diff"} onClick={() => setView("diff")} className={tabCls(view === "diff")}>
              Changes
            </button>
            <button type="button" aria-pressed={view === "split"} onClick={() => setView("split")} className={tabCls(view === "split")}>
              Side by side
            </button>
          </div>
        )}
      </div>
      {rows && view === "split" ? (
        <div aria-label={`${change.label}, side by side`} className="cq-scroll max-h-80 overflow-auto bg-card font-mono text-[0.6875rem] leading-[1.55]">
          <div aria-hidden="true" className="grid grid-cols-2 border-b border-slate-200 bg-slate-50 font-sans text-[0.6875rem] font-medium">
            <span className="px-3 py-0.5 text-rose-700 dark:text-rose-300">Before</span>
            <span className="border-l border-slate-200 px-3 py-0.5 text-emerald-700 dark:text-emerald-300">After</span>
          </div>
          {sideBySide(all ? rows : folded(rows)).map((p, idx) =>
            p.gap ? (
              <GapButton key={`gap-${idx}`} count={p.gap.count} onClick={() => setAll(true)} />
            ) : (
              <div key={idx} className="grid grid-cols-2">
                <SplitCell line={p.left} />
                <SplitCell line={p.right} right />
              </div>
            ),
          )}
        </div>
      ) : rows ? (
        <div className="cq-scroll max-h-80 overflow-auto bg-card font-mono text-[0.6875rem] leading-[1.55]">
          {(all ? rows : folded(rows)).map((r, idx) =>
            r.t === "gap" ? (
              <GapButton key={`gap-${idx}`} count={r.count} onClick={() => setAll(true)} />
            ) : (
              <div key={idx} className={`flex ${ROW[r.t]}`}>
                <span aria-hidden="true" className="w-9 flex-none select-none pr-1 text-right text-slate-400">{r.o ?? ""}</span>
                <span aria-hidden="true" className="w-9 flex-none select-none pr-1 text-right text-slate-400">{r.n ?? ""}</span>
                <span aria-label={r.t === "del" ? "removed" : r.t === "add" ? "added" : undefined} className="w-4 flex-none select-none text-center">
                  {SIGN[r.t]}
                </span>
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words pr-3">{r.text || " "}</span>
              </div>
            ),
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 p-2 md:flex-row">
          <Block title="Before" text={change.before} tone="text-rose-600" />
          <Block title="After" text={change.after} tone="text-emerald-700" />
        </div>
      )}
    </div>
  );
}

/* A snapshot's variables as {NAME: value}, whether kept as a map (of values
   or of {value}) or as a list of {name, value}. */
function valuesOf(vars) {
  if (!vars || typeof vars !== "object") return {};
  const pairs = Array.isArray(vars) ? vars.map((v) => [v?.name, v?.value]) : Object.entries(vars).map(([k, v]) => [k, v && typeof v === "object" ? v.value : v]);
  return Object.fromEntries(pairs.filter(([k]) => k));
}

/* The values two versions' variables differ in, as rows of {name, from, to}. */
export function variableRows(before, after) {
  const a = valuesOf(before);
  const b = valuesOf(after);
  return [...new Set([...Object.keys(a), ...Object.keys(b)])]
    .filter((n) => (a[n] ?? "") !== (b[n] ?? ""))
    .map((name) => ({ name, from: a[name], to: b[name] }));
}

/* Each changed field: variables as value rows, text as a line diff, and what
   was recorded or removed. `raw` holds the two snapshots. */
function Fields({ changes, raw = {} }) {
  return (
    <div className="space-y-3">
      {changes.map((c) =>
        c.key === "variables" && (raw.before || raw.after) ? (
          <div key={c.key}>
            <p className="mb-1.5 text-[0.75rem] font-semibold text-slate-800">{raw.before ? "Variables" : "Variables, as created"}</p>
            <VariableChanges rows={variableRows(raw.before?.variables, raw.after?.variables)} />
          </div>
        ) : c.before !== null && c.after !== null ? (
          <FieldDiff key={c.key} change={c} />
        ) : (
          <div key={c.key}>
            <p className="mb-1 text-[0.75rem] font-semibold text-slate-700">{c.after !== null ? `${c.label}, as created` : `${c.label}, removed`}</p>
            <Block title={c.after !== null ? "Recorded" : "Removed"} text={c.after ?? c.before} tone={c.after !== null ? "text-emerald-700" : "text-rose-600"} />
          </div>
        ),
      )}
    </div>
  );
}

const MADE = /^(test|artifact)\.(created|recreated)$/;

/* The story of this Test ID (or its Artifact) so far, among the loaded
   entries: when it was first made, by whom, and how often it was made again.
   Everything is told by the Test ID, which stays the same however often the
   Test is re-created. */
export function historyOf(e, items = []) {
  const m = MADE.exec(String(e.action));
  if (!m || !e.test_key) return "";
  const kind = m[1];
  const what = kind === "test" ? e.test_key : `The Artifact of ${e.test_key}`;
  const made = items
    .filter((o) => o.test_key === e.test_key && MADE.test(String(o.action)) && String(o.action).startsWith(`${kind}.`))
    .sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
  if (!made.includes(e)) return "";
  const first = made.find((o) => o.action.endsWith(".created"));
  const again = made.filter((o) => o.action.endsWith(".recreated"));
  const after = made.slice(made.indexOf(e) + 1).length;
  const times = (n) => (n === 1 ? "once" : n === 2 ? "twice" : `${n} times`);
  const parts = [];
  if (first) parts.push(`${what} was first created on ${formatWhen(first.occurred_at)} by ${first.user_name || "an unknown user"}`);
  if (again.length) parts.push(`${first ? "and " : `${what} was `}re-created ${times(again.length)} since`);
  let line = parts.length ? `${parts.join(", ")}.` : "";
  if (made.length > 1) line += after ? ` ${after === 1 ? "One later re-create" : `${after} later re-creates`} replaced this one.` : " This is the latest.";
  return line.trim();
}

/* What changed (ActivityPanel's renderExtra, given the loaded entries): the
   Test ID's history in one sentence, then each field this change touched (a
   creation shows what it recorded). Variable values are shown by
   AppPageInline instead. */
export function AppPageChanges(e, ctx = {}) {
  if (variableChanges(e)) return null;
  const history = historyOf(e, ctx.items);
  const changes = changesOf(e);
  if (!history && !changes.length) return null;
  return (
    <div className="space-y-3">
      {history && (
        <p className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[0.8125rem] leading-relaxed text-slate-700">
          <Icons.Clock className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
          {history}
        </p>
      )}
      {changes.length > 0 && <Fields changes={changes} raw={{ before: e.before, after: e.after }} />}
    </div>
  );
}

/* A variable-values change as rows of {name, from, to}, or null. */
export function variableChanges(e) {
  if (Array.isArray(e.details?.changes)) return e.details.changes;
  if (e.action !== "test.values_changed") return null;
  const before = e.before || {};
  const after = e.after || {};
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].map((name) => ({ name, from: before[name], to: after[name] }));
}

/* A variable's name as people say it: EQUIPMENT_NAME → "Equipment name",
   BASE_URL → "Base URL", FIELD_REPORTEDBY → "Reported by" (a field's id
   prefix dropped, a common last word run into the name split off). */
const ACRONYMS = new Set(["URL", "ID", "API", "PIN", "QA", "UI", "SKU", "UOM"]);
const PREFIX = /^(?:FIELD|FLD|TXT|DDL|INPUT|SELECT)_(?=.)/i;
const TAIL = /^(.{3,}?)(BY|NAME|CODE|DATE|TIME|TYPE|NUMBER|QTY|STATUS)$/i;
export function humanName(name) {
  const words = String(name || "")
    .replace(PREFIX, "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .flatMap((w) => {
      const m = TAIL.exec(w);
      return m && !ACRONYMS.has(w.toUpperCase()) ? [m[1], m[2]] : [w];
    });
  if (!words.length) return String(name || "");
  return words
    .map((w, i) => {
      const up = w.toUpperCase();
      if (ACRONYMS.has(up)) return up;
      const low = w.toLowerCase();
      return i === 0 ? low.charAt(0).toUpperCase() + low.slice(1) : low;
    })
    .join(" ");
}

const isEmpty = (v) => v === null || v === undefined || v === "";

/* What happened to one value, in a word. */
export function changeKind(c) {
  if (c.from === MASK || c.to === MASK) return "secret";
  if (isEmpty(c.from)) return "set";
  if (isEmpty(c.to)) return "cleared";
  return "changed";
}

const KIND = {
  set: { label: "Set", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/15 dark:text-emerald-300" },
  changed: { label: "Changed", cls: "bg-brand-500/10 text-brand-700 ring-brand-600/20 dark:text-brand-300" },
  cleared: { label: "Cleared", cls: "bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/15 dark:text-rose-300" },
  secret: { label: "Secret changed", cls: "bg-slate-100 text-slate-600 ring-slate-500/20" },
};

function Pill({ v, was }) {
  if (isEmpty(v)) return <span className="text-[0.75rem] italic text-slate-400">empty</span>;
  return (
    <span
      className={`inline-block max-w-full break-words rounded-md px-2 py-0.5 font-mono text-[0.75rem] ${
        was
          ? "bg-rose-50 text-rose-800 line-through decoration-rose-400/70 dark:bg-rose-500/15 dark:text-rose-200"
          : "bg-emerald-50 font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200"
      }`}
    >
      <span className="sr-only">{was ? "was " : "now "}</span>
      {String(v)}
    </span>
  );
}

/* Each changed value: its name, what happened, and was → now. A secret's
   value is never kept, so it only says that it changed. */
function VariableChanges({ rows }) {
  return (
    <ul aria-label="Values changed" className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-card">
      {rows.map((c) => {
        const kind = changeKind(c);
        return (
          <li key={c.name} className="grid grid-cols-1 gap-x-4 gap-y-1.5 px-3 py-2.5 sm:grid-cols-[minmax(9rem,14rem)_1fr]">
            <div className="min-w-0">
              <p className="truncate text-[0.8125rem] font-semibold text-slate-800" title={humanName(c.name)}>{humanName(c.name)}</p>
              <p className="truncate font-mono text-[0.625rem] text-slate-400">{c.name}</p>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className={`inline-flex flex-none items-center rounded-full px-1.5 py-px text-[0.6875rem] font-medium capitalize ring-1 ring-inset ${KIND[kind].cls}`}>
                {KIND[kind].label}
              </span>
              {kind === "secret" ? (
                <span className="inline-flex items-center gap-1 text-[0.75rem] text-slate-500">
                  <Icons.Lock className="h-3 w-3" aria-hidden="true" />
                  Value hidden: a secret is never kept.
                </span>
              ) : (
                <>
                  {kind !== "set" && <Pill v={c.from} was />}
                  {kind !== "cleared" && (
                    <span className="inline-flex min-w-0 items-center gap-2">
                      {kind === "changed" && <Icons.ArrowRight className="h-3.5 w-3.5 flex-none text-slate-400" />}
                      <Pill v={c.to} />
                    </span>
                  )}
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* What an entry always shows (ActivityPanel's renderInline): the values
   changed, or which fields a re-create changed and by how much. */
export function AppPageInline(e) {
  const vars = variableChanges(e);
  if (vars && e.script) {
    const script = AppPageChanges(e.script);
    return (
      <div className="space-y-3">
        {vars.length > 0 && <VariableChanges rows={vars} />}
        {script}
      </div>
    );
  }
  if (vars) return vars.length ? <VariableChanges rows={vars} /> : null;
  const changed = changesOf(e).filter((c) => c.before !== null && c.after !== null);
  if (!changed.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {changed.map((c) => {
        const rows = lineDiff(c.before, c.after);
        return (
          <span key={c.key} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-card px-2 py-0.5 text-[0.6875rem] text-slate-700">
            <Icons.Pencil className="h-3 w-3 text-slate-400" aria-hidden="true" />
            {c.label}
            {rows ? <Counts {...counts(rows)} /> : <span className="text-slate-400">changed</span>}
          </span>
        );
      })}
    </div>
  );
}

/* An entry's sentence (ActivityPanel's summaryOf): a value change in words
   (the stored message repeats every value, shown below as rows instead). */
/* A generation's internal id says nothing to a reader: "TEST-RD-9 recreated by
   generation 7a8c…" reads "TEST-RD-9 recreated". */
const plain = (m) => (m ? String(m).replace(/\s+(?:by|from) generation [0-9a-f-]{8,}/gi, "") : m);

/* A delete's title says what was deleted: "TEST-RD-1: Test deleted",
   "ART-RD-1: Artifact deleted" (the message, in Record, says the rest). */
const DELETED = { test: "Test", artifact: "Artifact", execution: "Execution" };

export function appPageSummary(e) {
  const [thing, verb] = String(e.action || "").split(".");
  if (verb === "deleted" && DELETED[thing] && keyOf(e)) return `${keyOf(e)}: ${DELETED[thing]} deleted`;
  const vars = variableChanges(e);
  if (!vars || !vars.length) return plain(e.message);
  const names = vars.map((c) => humanName(c.name));
  const list =
    names.length > 3 ? `${names.slice(0, 3).join(", ")} and ${names.length - 3} more` : names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  const what = `${list} changed`;
  return e.test_key ? `${e.test_key}: ${what}` : what;
}

/* The key an entry is about: its Artifact's, execution's or Test's. */
function keyOf(e) {
  const thing = String(e.action || "").split(".")[0];
  if (thing === "artifact" && e.artifact_key) return e.artifact_key;
  if (thing === "execution" && e.execution_key) return e.execution_key;
  return e.test_key || e.artifact_key || e.execution_key || "";
}

/* A row's one line: the ID first, then what happened ("ART-RD-1: script
   given the new variable values"). */
export function appPageTitle(e) {
  const text = appPageSummary(e) || "";
  const key = keyOf(e);
  if (!key || text.startsWith(key)) return text;
  return text ? `${key}: ${text}` : key;
}

/* A few words on the entry's row (ActivityPanel's hintOf): how many values
   changed, or how many lines a re-create added and removed. */
export function appPageHint(e) {
  const vars = variableChanges(e);
  if (vars && e.script) {
    const script = appPageHint(e.script);
    return (
      <span className="inline-flex items-center gap-1.5">
        {vars.length > 0 && <span className="text-[0.6875rem] text-slate-500">{vars.length} {vars.length === 1 ? "value" : "values"}</span>}
        {script}
      </span>
    );
  }
  if (vars) return vars.length ? <span className="text-[0.6875rem] text-slate-500">{vars.length} {vars.length === 1 ? "value" : "values"}</span> : null;
  const changed = changesOf(e).filter((c) => c.before !== null && c.after !== null);
  const total = changed.reduce(
    (t, c) => {
      const rows = lineDiff(c.before, c.after);
      if (!rows) return t;
      const n = counts(rows);
      return { added: t.added + n.added, removed: t.removed + n.removed };
    },
    { added: 0, removed: 0 },
  );
  return total.added || total.removed ? <Counts {...total} /> : null;
}

/* Details lists every detail field except the variable changes (shown inline). */
const appPageDetails = (e) => Object.entries(e.details || {}).filter(([k]) => k !== "changes");

/* ActivityButton for an App Page (`scope` {app_page_id}) or a Test ID (`scope`
   {test_key}: the Test, its Artifact and its executions, however often it was
   re-created). It opens maximized; `withExecutions` (App Page Executions only, where it opens on it)
   adds the Executions filter. `openOn` picks the filter it opens on instead. */
export default function AppPageActivityButton({
  title,
  scope,
  testKey = "",
  compact = false,
  withExecutions = false,
  openOn,
  className = "",
}) {
  return (
    <ActivityButton
      title={title}
      scope={scope}
      testKey={testKey}
      compact={compact}
      className={className}
      label="Logs"
      tooltip={compact ? "Log" : "Logs"}
      heading="Logs"
      overview={false}
      dayHeadings={false}
      multiPerson
      sinceDays={30}
      closeOnlyWithX
      auditRecord={false}
      titleOf={appPageTitle}
      kindOf={appPageKind}
      combine={combineAppPageItems}
      endpoint="app-pages/activity"
      groups={withExecutions ? APP_PAGE_EXECUTION_GROUPS : APP_PAGE_GROUPS}
      defaultGroup={openOn ?? (withExecutions ? "execution" : "")}
      actions={APP_PAGE_ACTIONS}
      testScope={testKey ? { test_key: testKey } : null}
      factsOf={appPageFacts}
      renderExtra={AppPageChanges}
      renderInline={AppPageInline}
      summaryOf={appPageSummary}
      hintOf={appPageHint}
      detailsOf={appPageDetails}
      startMaximized
    />
  );
}
